import SearchIcon from "@mui/icons-material/Search";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import type Individual from "marketplace/individuals/individual";
import { type PermissionKind, useGetPermissionKindsQuery } from "permissions/_api/permission-kinds-api-service";
import { kindPermissionsPath, matchingKinds, offeredKinds } from "permissions/_views/kinds/permission-kinds-model";
import { useKindHolderCount } from "permissions/_views/kinds/use-kind-holders";
import { personPermissionsPath } from "permissions/_views/people/person-permissions-path";
import { permissionAreaToken } from "permissions/_views/permission-areas";
import useRecordSearch from "permissions/_views/search/use-record-search";
import {
  PERMISSION_GRANTEE_SEARCH_MAXIMUM_LENGTH,
  usePermissionGranteeTypeahead,
} from "permissions/_views/search/use-permission-grantee-typeahead";

import { holdersLabel, kindAllows, kindLabel } from "permissions/_views/wording/permission-wording";
import useSessionUser from "permissions/session/use-session-user";
import { type RefObject, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";

/**
 * The permissions search, in the header of every permissions page (`docs/design/permissions/
 * PERMISSIONS_UI_DESIGN.md`, "Page header, search and back"): one box that changes the page's
 * subject without going home.
 *
 * Its results are grouped. **Kinds** come from the kinds endpoint and are matched here, on the name
 * and the "may …" sentence, as the reader types; **People** come from the grantee typeahead once two
 * characters are typed; **Records** come from each centre's admin-gated permission-targets search
 * (#6174), which finds the organization's records by name whether or not the viewer can open them,
 * and open the record's page. Each option's
 * accessible name starts with its group noun, so a screen reader hears "Kind: Signing authority"
 * rather than a bare name. Choosing one opens its page.
 *
 * `/` puts the cursor in the box from anywhere on the page that is not already taking text.
 */

export const PERMISSIONS_SEARCH_LABEL = "Find a person, a record, or a kind of permission";

export const SEARCH_GROUPS = { kinds: "Kinds", people: "People", records: "Records" } as const;

type SearchOption =
  | Readonly<{ group: typeof SEARCH_GROUPS.kinds; noun: "Kind"; key: string; label: string; secondary: string; path: string; kind: PermissionKind }>
  | Readonly<{
      group: typeof SEARCH_GROUPS.people;
      noun: "Person";
      key: string;
      label: string;
      secondary?: string;
      path: string;
      individual?: Individual;
    }>
  | Readonly<{ group: typeof SEARCH_GROUPS.records; noun: "Record"; key: string; label: string; secondary: string; path: string; kind: PermissionKind }>;

/** True when a key pressed there is text for that element, not a shortcut for the page. */
function takesText(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
}

/** Focuses `input` when `/` is pressed anywhere that is not taking text. */
function useSlashToFocus(input: RefObject<HTMLInputElement | null>): void {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "/" || event.ctrlKey || event.metaKey || event.altKey || takesText(event.target)) return;
      event.preventDefault();
      input.current?.focus();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [input]);
}

function KindHolderChip(props: Readonly<{ kind: PermissionKind }>) {
  const count = useKindHolderCount(props.kind);
  if (count === undefined) return null;
  return <Chip size="small" variant="outlined" label={holdersLabel(count)} sx={{ flexShrink: 0 }} />;
}

/** Two lines, the second clamped to two lines, with the kind's holder count as a trailing chip. */
function OptionContent(props: Readonly<{ option: SearchOption }>) {
  const { option } = props;
  const token = option.group === SEARCH_GROUPS.people ? undefined : permissionAreaToken(option.kind.area);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%", minWidth: 0 }}>
      {option.group === SEARCH_GROUPS.people ? (
        <IndividualAvatar individual={option.individual} />
      ) : (
        token && <token.Icon fontSize="small" sx={{ color: token.color, flexShrink: 0 }} aria-hidden />
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: "anywhere" }}>
          {option.label}
        </Typography>
        {option.secondary && (
          <Typography
            variant="caption"
            color="text.secondary"
            component="p"
            sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", overflowWrap: "anywhere" }}
          >
            {option.secondary}
          </Typography>
        )}
      </Box>
      {option.group === SEARCH_GROUPS.kinds && <KindHolderChip kind={option.kind} />}
    </Box>
  );
}

/** Stable while the kinds load, so the records search's arguments do not change each render. */
const NO_KINDS: readonly PermissionKind[] = [];

function capitalized(text: string): string {
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
}

/** What could not be searched, when something could not. The rest of the results are still listed. */
function searchFailure(peopleFailed: boolean, recordsPartial: boolean): string | undefined {
  if (peopleFailed && recordsPartial) return "Unable to search people or some records.";
  if (peopleFailed) return "Unable to search people.";
  if (recordsPartial) return "Unable to search some records.";
  return undefined;
}

export default function PermissionsSearch(props: Readonly<{ focusOnOpen?: boolean }>) {
  const navigate = useNavigate();
  const sessionUser = useSessionUser();
  const mayAdministerPlatform = sessionUser?.canAdministerPlatform() ?? false;
  const kindsQuery = useGetPermissionKindsQuery();
  const [text, setText] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  useSlashToFocus(inputRef);

  // The home page is the search with nothing chosen yet (the design's "the box with focus"), so it
  // opens with the cursor in it. Every other page leaves focus where the reader's last step put it.
  const focusOnOpen = props.focusOnOpen ?? false;
  useEffect(() => {
    if (focusOnOpen) inputRef.current?.focus();
  }, [focusOnOpen]);

  const people = usePermissionGranteeTypeahead(text);
  const offered = useMemo(() => offeredKinds(kindsQuery.data?.kinds ?? [], mayAdministerPlatform), [kindsQuery.data, mayAdministerPlatform]);
  // Every kind, not the offered ones: those are the standing kinds, and records are what the per-record kinds are granted on.
  const records = useRecordSearch(text, kindsQuery.data?.kinds ?? NO_KINDS, sessionUser?.canReadMemberPermissions() ?? false);

  const options = useMemo<SearchOption[]>(() => {
    const kindOptions: SearchOption[] = matchingKinds(offered, text).map((kind) => ({
      group: SEARCH_GROUPS.kinds,
      noun: "Kind",
      key: `kind-${kind.slug}`,
      label: kindLabel(kind),
      secondary: kindAllows(kind),
      path: kindPermissionsPath(kind),
      kind,
    }));
    const personOptions: SearchOption[] = people.options.map((person) => ({
      group: SEARCH_GROUPS.people,
      noun: "Person",
      key: `person-${person.value}`,
      label: person.label,
      secondary: person.secondaryLabel,
      path: personPermissionsPath(new Guid(person.value)),
      individual: person.individual,
    }));
    const recordOptions: SearchOption[] = records.results.map((record) => ({
      group: SEARCH_GROUPS.records,
      noun: "Record",
      key: `record-${record.resourceType}-${record.id}`,
      label: record.name,
      secondary: capitalized(record.kind.targetNoun ?? "record"),
      path: record.path,
      kind: record.kind,
    }));
    return [...kindOptions, ...personOptions, ...recordOptions];
  }, [offered, people.options, records.results, text]);

  let noOptionsText = "Type a name, an email, a record, or what a permission allows";
  if (people.isSearchTextTooLong) noOptionsText = `Use ${PERMISSION_GRANTEE_SEARCH_MAXIMUM_LENGTH} characters or fewer`;
  else if (people.isSearchReady) noOptionsText = "Nothing matches";

  return (
    <Autocomplete<SearchOption, false, false, false>
      value={null}
      inputValue={text}
      options={options}
      groupBy={(option) => option.group}
      filterOptions={(all) => all}
      getOptionLabel={(option) => option.label}
      getOptionKey={(option) => option.key}
      loading={people.isFetching || records.isFetching}
      loadingText="Searching…"
      noOptionsText={noOptionsText}
      clearOnBlur={false}
      blurOnSelect
      onInputChange={(_event, value, reason) => {
        if (reason === "input" || reason === "clear") setText(value);
      }}
      onChange={(_event, option) => {
        if (!option) return;
        setText("");
        void navigate(option.path);
      }}
      renderOption={({ key, ...optionProps }, option) => (
        <li key={key} {...optionProps} aria-label={`${option.noun}: ${option.label}${option.secondary ? `, ${option.secondary}` : ""}`}>
          <OptionContent option={option} />
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={PERMISSIONS_SEARCH_LABEL}
          size="small"
          inputRef={inputRef}
          error={people.isError || records.isPartial}
          helperText={searchFailure(people.isError, records.isPartial)}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
            htmlInput: { ...params.slotProps.htmlInput, maxLength: PERMISSION_GRANTEE_SEARCH_MAXIMUM_LENGTH, "aria-keyshortcuts": "/" },
          }}
        />
      )}
    />
  );
}
